import type { MixedItemTag } from "@memorang/types";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
	collectAncestorIds,
	hasRenderableSubtree,
} from "../../helpers/tag-tree";
import type { CurrentView } from "../../types";

export type TagSection = { title: string; tags: MixedItemTag[] };

export type VisibleRow = {
	tag: MixedItemTag;
	level: number;
	hasChildren: boolean;
	hasCheckedDescendant: boolean;
};

const collectIdsToExpand = (
	tags: MixedItemTag[],
	targetIds: Set<number>,
): number[] => {
	const expanded: number[] = [];
	for (const id of targetIds) {
		expanded.push(...collectAncestorIds(tags, id));
	}
	return expanded;
};

const subtreeHasCheckedDescendant = (
	tag: MixedItemTag,
	map?: Record<string, boolean>,
): boolean => {
	if (!tag.children?.length) {
		return false;
	}
	for (const child of tag.children) {
		if (map?.[child.id]) {
			return true;
		}
		if (subtreeHasCheckedDescendant(child, map)) {
			return true;
		}
	}
	return false;
};

const flattenVisibleRows = (
	rootTags: MixedItemTag[],
	expandedMap: Record<number, boolean>,
	selectedMap: Record<string, boolean> | undefined,
	maxFilterDepth: number,
	currentView: CurrentView,
): VisibleRow[] => {
	const rows: VisibleRow[] = [];
	const walk = (tag: MixedItemTag, level: number) => {
		const childDepthAllowed = level + 2 <= maxFilterDepth;
		const renderableChildren = childDepthAllowed
			? (tag.children || []).filter((child) =>
					hasRenderableSubtree(child, currentView),
				)
			: [];
		const hasChildren = renderableChildren.length > 0;
		rows.push({
			tag,
			level,
			hasChildren,
			hasCheckedDescendant: subtreeHasCheckedDescendant(tag, selectedMap),
		});
		if (hasChildren && expandedMap[tag.id]) {
			for (const child of renderableChildren) {
				walk(child, level + 1);
			}
		}
	};
	for (const top of rootTags) {
		walk(top, 0);
	}
	return rows;
};

export type UseTagFilterSectionsParams = {
	sectionTags: TagSection[];
	selectedCheckboxMap?: Record<string, boolean>;
	maxFilterDepth: number;
	currentView?: CurrentView;
};

export const useTagFilterSections = ({
	sectionTags,
	selectedCheckboxMap,
	maxFilterDepth,
	currentView = "Filter",
}: UseTagFilterSectionsParams) => {
	const [expandedMap, setExpandedMap] = useState<Record<number, boolean>>({});

	const checkedIds = useMemo(() => {
		const ids = new Set<number>();
		if (!selectedCheckboxMap) {
			return ids;
		}
		for (const key of Object.keys(selectedCheckboxMap)) {
			if (selectedCheckboxMap[key]) {
				ids.add(Number(key));
			}
		}
		return ids;
	}, [selectedCheckboxMap]);

	const allTopLevelTags = useMemo(
		() => sectionTags.flatMap((section) => section.tags),
		[sectionTags],
	);

	useEffect(() => {
		if (checkedIds.size === 0) {
			return;
		}
		const ancestorIds = collectIdsToExpand(allTopLevelTags, checkedIds);
		if (ancestorIds.length === 0) {
			return;
		}
		setExpandedMap((prev) => {
			let changed = false;
			const next = { ...prev };
			for (const id of ancestorIds) {
				if (!next[id]) {
					next[id] = true;
					changed = true;
				}
			}
			return changed ? next : prev;
		});
	}, [checkedIds, allTopLevelTags]);

	const setExpanded = useCallback((id: number, value: boolean) => {
		setExpandedMap((prev) => ({ ...prev, [id]: value }));
	}, []);

	const visibleSections = useMemo(() => {
		return sectionTags
			.map(({ title, tags }) => {
				const renderableTags = tags.filter((tag) =>
					hasRenderableSubtree(tag, currentView),
				);
				return {
					title,
					rows: flattenVisibleRows(
						renderableTags,
						expandedMap,
						selectedCheckboxMap,
						maxFilterDepth,
						currentView,
					),
				};
			})
			.filter((section) => section.rows.length > 0);
	}, [
		sectionTags,
		expandedMap,
		selectedCheckboxMap,
		maxFilterDepth,
		currentView,
	]);

	return { visibleSections, expandedMap, setExpanded };
};
